import { FC, useCallback, useMemo, useState } from 'react';
import useThread from '@/hooks/use-thread.hook';
import { useQuery } from 'react-query';
import { getAvailableShortcuts } from '@/lib/services/shortcut.service';
import SidebarList from './sidebar-list';
import { QueryKey } from '@/lib/query-client';
import useMessageTray from '@/hooks/use-message-tray.hook';
import { ShortcutModel } from '@/lib/models/shortcut.model';
import Tab from './tab';
import TabHeader from './tab/tab-header';
import TabContent from './tab/tab-content';
import SidebarSearch from './sidebar-list/sidebar-search';

interface Props {
  onLoadShortcut?: () => void;
  onClose?: () => void;
}

const ShortcutTab: FC<Props> = ({ onLoadShortcut, onClose }) => {
  const [keyword, setKeyword] = useState('');

  const { loadShortcut } = useThread();
  const { closeAll } = useMessageTray();

  const handleKeywordChange = (value: string) => {
    setKeyword(value);
  };

  const handleLoadShortcut = useCallback(
    (shortcut: ShortcutModel) => {
      onLoadShortcut?.();
      loadShortcut(shortcut);
      closeAll();
    },
    [closeAll, loadShortcut, onLoadShortcut]
  );

  const { data = [], isLoading } = useQuery([QueryKey.ShortcutsList], async () =>
    getAvailableShortcuts()
  );

  const listItems = useMemo(() => {
    const safeKeyword = String(keyword).trim().toLowerCase();

    const filteredData = data.filter(
      ({ name, description }) =>
        String(name).toLowerCase().includes(safeKeyword) ||
        String(description).toLowerCase().includes(safeKeyword)
    );

    return filteredData.map((shortcut) => {
      const { id, name, description } = shortcut;

      return {
        key: id,
        title: name,
        content: description,
        onClick: () => handleLoadShortcut(shortcut),
      };
    });
  }, [data, keyword, handleLoadShortcut]);

  return (
    <Tab onClose={onClose}>
      <TabHeader icon="shortcut" title="Shortcuts" />
      <TabContent>
        <SidebarSearch
          name="shortcut-search"
          value={keyword}
          onChange={handleKeywordChange}
          placeholder="Search shortcuts"
        />
      </TabContent>
      <SidebarList isLoading={isLoading} items={listItems} emptyMessage="No shortcuts found" />
    </Tab>
  );
};

export default ShortcutTab;
